import PageTitle from '../../components/UI/PageTitle/PageTitle';
import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import styles from './Dashboard.module.css';
import { useAppDispatch, useAppSelector } from '../../store/store';
import {
  SalesOrderInfo,
  fetchSalesOrders,
  selectSalesOrders,
  fetchServicesTranslations,
  selectServicesTranslations,
} from '../../store/slices/salesOrders';
import { IDoctor, fetchDoctors, selectDoctors } from '../../store/slices/doctors';
import {
  IAccountInfo,
  IPerson,
  selectAccountInfo,
  selectLocationId,
  selectPersonLoggedIn,
} from '../../store/slices/auth';
import { ConsultationTypes, IS_CONSULTATION } from '../../enums/enums';
import { useTranslation } from '../../hooks/useTranslations';
import { buildTranslatedServicesMap, getTranslatedServicesName } from '../../utils/miscUtils';
import { getFullNameInitials } from '../../helpers';
import { NotificationObj, selectNotifications } from '../../store/slices/notifications';
import checkIcon from '../../assets/icons/check.svg';
import calendarIcon from '../../assets/icons/calendar.svg';
import timeIcon from '../../assets/icons/time.svg';
import chatsIcon from '../../assets/icons/chatsvg.svg';

type DashboardAppointment = {
  id: string;
  customerName: string;
  serviceName: string;
  start?: Date;
  end?: Date;
  createdAt: Date;
  isConsultation: boolean;
  isDiagnostic: boolean;
  doctorName?: string;
  statusId?: number;
};

type SummaryCardConfig = {
  id: 'upcoming' | 'parked' | 'chats' | 'tasks';
  icon: string;
  value: number;
  label: string;
};

const mapSalesOrderToDashboardAppointment = (
  salesOrder: SalesOrderInfo,
  doctors: IDoctor[],
  translatedServicesMap: Map<string, string>
): DashboardAppointment => {
  const appointmentStart = salesOrder.sales_order_appointment.start
    ? new Date(salesOrder.sales_order_appointment.start)
    : undefined;
  const appointmentEnd = salesOrder.sales_order_appointment.end_time
    ? new Date(salesOrder.sales_order_appointment.end_time)
    : undefined;

  const doctorAssigned = doctors.find((doctor) =>
    doctor.person_appointments.find(
      (appointment) =>
        appointment.sales_order_id == salesOrder.sales_order_item.sales_order_id &&
        appointment.sales_order_item_id == salesOrder.sales_order_item.sales_order_item_id
    )
  );

  const isDiagnostic =
    salesOrder.service_service_group.service_group_id === 1 ||
    salesOrder.service_service_group.service_group_id === 4;

  const isConsultation = IS_CONSULTATION.includes(
    salesOrder.service_service_group.service_group_id
  );

  return {
    id: `${salesOrder.sales_order.sales_order_id}-${salesOrder.sales_order_item.sales_order_item_id}`,
    customerName: `${salesOrder.customer_person.person_firstname} ${salesOrder.customer_person.person_lastname}`,
    serviceName: getTranslatedServicesName(
      salesOrder.service.service_id,
      translatedServicesMap,
      salesOrder.service.service_name
    ),
    start: appointmentStart,
    end: appointmentEnd,
    createdAt: new Date(salesOrder.sales_order.datetime_create),
    isConsultation,
    isDiagnostic,
    doctorName: doctorAssigned
      ? `${doctorAssigned.person_firstname} ${doctorAssigned.person_lastname}`
      : undefined,
    statusId: salesOrder.work_order?.work_order_status_id,
  };
};

const computeUpcomingAppointments = (appointments: DashboardAppointment[]): DashboardAppointment[] => {
  const todayStart = moment().startOf('day');

  return appointments
    .filter((appt) => {
      if (!appt.start) return false;
      if (appt.statusId === 2 || appt.statusId === 3 || appt.statusId === 4) return false;

      const startMoment = moment(appt.start);
      // Include all appointments whose appointment DATE is today or in the future
      return startMoment.isSameOrAfter(todayStart, 'day');
    })
    .sort((a, b) => {
      if (!a.start || !b.start) return 0;
      return b.start.getTime() - a.start.getTime();    });
};

const computeParkedConsultations = (appointments: DashboardAppointment[]): DashboardAppointment[] =>
  appointments
    .filter((appt) => appt.isConsultation && appt.statusId === 4)
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

export const Dashboard = () => {
  const dispatch = useAppDispatch();
  const { translations } = useTranslation();

  const salesOrders = useAppSelector(selectSalesOrders);
  const servicesTranslations = useAppSelector(selectServicesTranslations);
  const doctors = useAppSelector<IDoctor[]>(selectDoctors);
  const accountInfo = useAppSelector<IAccountInfo>(selectAccountInfo);
  const locationId = useAppSelector<string>(selectLocationId);
  const personLoggedIn = useAppSelector<IPerson | undefined>(selectPersonLoggedIn);
  const notifications = useAppSelector(selectNotifications);

  const lang = (localStorage.getItem('lang') || 'en').toLowerCase().trim();

  const [showAllUpcoming, setShowAllUpcoming] = useState(false);
  const [showAllParked, setShowAllParked] = useState(false);

  useEffect(() => {
    dispatch(fetchSalesOrders());
    dispatch(fetchServicesTranslations());
  }, [dispatch]);

  useEffect(() => {
    if (locationId) {
      dispatch(fetchDoctors());
    }
  }, [dispatch, locationId]);

  const translatedServicesMap = useMemo(
    () => buildTranslatedServicesMap(servicesTranslations, lang),
    [servicesTranslations, lang]
  );

  const doctorForLoggedInUser = useMemo(
    () =>
      doctors.find((doctor) => parseInt(doctor.person_id) === Number(personLoggedIn?.person_id)),
    [doctors, personLoggedIn]
  );

  const relevantSalesOrders: SalesOrderInfo[] = useMemo(() => {
    return salesOrders.filter((salesOrder) => {
      if (
        !accountInfo.serves_consultations &&
        IS_CONSULTATION.includes(salesOrder.service_service_group.service_group_id)
      )
        return false;

      if (
        !accountInfo.serves_diagnostics &&
        !IS_CONSULTATION.includes(salesOrder.service_service_group.service_group_id)
      )
        return false;

      if (doctorForLoggedInUser) {
        const isAssignedToDoctor = doctorForLoggedInUser.person_appointments.find(
          (appointment) =>
            appointment.sales_order_id == salesOrder.sales_order.sales_order_id &&
            appointment.sales_order_item_id == salesOrder.sales_order_item.sales_order_item_id
        );
        if (!isAssignedToDoctor) return false;
      }

      return true;
    });
  }, [salesOrders, accountInfo, doctorForLoggedInUser]);

  const allAppointments: DashboardAppointment[] = useMemo(
    () =>
      relevantSalesOrders.map((so) =>
        mapSalesOrderToDashboardAppointment(so, doctors, translatedServicesMap)
      ),
    [relevantSalesOrders, doctors, translatedServicesMap]
  );

  const upcomingAppointments = useMemo(() => {
    return computeUpcomingAppointments(allAppointments).slice(0, 10);
  }, [allAppointments]);

  const parkedConsultations = useMemo(
    () => computeParkedConsultations(allAppointments),
    [allAppointments]
  );

  const upcomingAppointmentsCount = upcomingAppointments.length;
  const parkedConsultationsCount = parkedConsultations.length;

  const visibleUpcomingAppointments = useMemo(
    () => (showAllUpcoming ? upcomingAppointments : upcomingAppointments.slice(0, 4)),
    [showAllUpcoming, upcomingAppointments]
  );

  const visibleParkedConsultations = useMemo(
    () => (showAllParked ? parkedConsultations : parkedConsultations.slice(0, 4)),
    [showAllParked, parkedConsultations]
  );

  // const openTasksCount = useMemo(
  //   () => tasks.filter((task) => task.status === 'open').length,
  //   [tasks]
  // );

  const openChatsCount = useMemo(() => {
    const chattingNotifications = notifications.filter(
      (el: NotificationObj) => el.type === 'chatting_message_received'
    );
    const uniqueConversations = chattingNotifications.filter(
      (el: NotificationObj, index: number, self: NotificationObj[]) =>
        index === self.findIndex((t: NotificationObj) => t.conversation_id === el.conversation_id)
    );
    return uniqueConversations.length;
  }, [notifications]);

  const summaryCards: SummaryCardConfig[] = useMemo(
    () => [
      {
        id: 'upcoming',
        icon: calendarIcon,
        value: upcomingAppointmentsCount,
        label: translations('upcoming.appointments'),
      },
      {
        id: 'parked',
        icon: timeIcon,
        value: parkedConsultationsCount,
        label: `${translations('parked')} ${translations('consultations')}`,
      },
      {
        id: 'chats',
        icon: chatsIcon,
        value: openChatsCount,
        label: `${translations('open')} ${translations('chats')}`,
      },
      {
        id: 'tasks',
        icon: checkIcon,
        value: 0,
        label: `${translations('open')} ${translations('tasks')}`,
      },
    ],
    [upcomingAppointmentsCount, parkedConsultationsCount, openChatsCount, translations]
  );

  const renderAppointmentRow = (appointment: DashboardAppointment) => {
    const initials = getFullNameInitials(appointment.customerName);
    const showTime = appointment.start;

    const now = Date.now();
    const timeDiffMs = appointment.start ? appointment.start.getTime() - now : undefined;
    const isWithinNext15Minutes =
      timeDiffMs !== undefined && timeDiffMs > 0 && timeDiffMs <= 15 * 60 * 1000;

    return (
      <div key={appointment.id} className={styles.appointmentRow}>
        <div
          className={`${styles.avatar} ${
            appointment.isDiagnostic
              ? styles.avatarDiagnostic
              : appointment.doctorName
              ? styles.avatarAssigned
              : styles.avatarUnassigned
          }`}
        >
          <span>{initials}</span>
        </div>
        <div className={styles.appointmentMain}>
          <div className={styles.appointmentName}>{appointment.customerName}</div>
          <div className={styles.appointmentMeta}>
            <span className={styles.appointmentService}>{appointment.serviceName}</span>
            {appointment.doctorName && (
              <div className={styles.appointmentDoctor}>
                {translations('label.assigned')} {appointment.doctorName}
              </div>
            )}
          </div>
        </div>
        <div className={styles.appointmentTimeBlock}>
          {showTime ? (
            <>
              <div
                className={
                  isWithinNext15Minutes
                    ? styles.appointmentTimeUpcomingUrgent
                    : styles.appointmentTimeUpcoming
                }
              >
                {moment(appointment.start).format('HH:mm')} -{' '}
                {appointment.end ? moment(appointment.end).format('HH:mm') : ''}
              </div>
              <div
                className={styles.appointmentRelativeTime}
                style={
                  isWithinNext15Minutes
                    ? { color: '#EF4444', fontWeight: 600 }
                    : undefined
                }
              >
                {moment(appointment.start).locale(lang).fromNow()}
              </div>
            </>
          ) : (
            <div className={styles.appointmentTime}>
              {moment(appointment.createdAt).format('DD.MM.YYYY')}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderParkedRow = (appointment: DashboardAppointment) => {
    const initials = getFullNameInitials(appointment.customerName);

    return (
      <div key={appointment.id} className={styles.appointmentRow}>
        <div className={`${styles.avatar} ${styles.avatarParked}`}>
          <span>{initials}</span>
        </div>
        <div className={styles.appointmentMain}>
          <div className={styles.appointmentName}>{appointment.customerName}</div>
          <div className={styles.appointmentMeta}>
            <span className={styles.appointmentService}>{appointment.serviceName}</span>
            {appointment.doctorName && (
              <span className={styles.appointmentDoctor}>
                {translations('label.assigned')} {appointment.doctorName}
              </span>
            )}
          </div>
        </div>
        <div className={styles.appointmentTimeBlock}>
          <div className={styles.appointmentTime}>
            {appointment.start
              ? moment(appointment.start).format('DD.MM.YYYY | HH:mm')
              : moment(appointment.createdAt).format('DD.MM.YYYY')}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.dashboard}>
      <div className={styles.headerRow}>
        <div>
          <PageTitle className={styles.title}>{translations('sidebar.dashboard')}</PageTitle>
        </div>
      </div>

      <div className={styles.summaryRow}>
        {summaryCards.map((card) => (
          <div key={card.id} className={styles.summaryCard}>
            <div className={styles.summaryContent}>
              <div className={styles.summaryIconWrapper}>
                <img src={card.icon} alt={card.label} className={styles.summaryIcon} />
              </div>
              <div className={styles.summaryText}>
                <div className={styles.summaryValue}>{card.value}</div>
                <div className={styles.summaryLabel}>{card.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.grid}>
        <section className={styles.card}>
          <div className={styles.cardHeader}>
            <h3 className={styles.cardTitle}>{translations('upcoming.appointments')} </h3>
            <span className={styles.cardMeta}>
              {upcomingAppointments.length}{' '}
              {/* {upcomingAppointments.length === 1
                ? translations('appointment')
                : translations('appointments')} */}
            </span>
          </div>
          <div className={styles.cardBody}>
            {upcomingAppointments.length === 0 ? (
              <div className={styles.emptyState}>{translations('no.upcoming.appointments')}</div>
            ) : (
              <>
                {visibleUpcomingAppointments.map(renderAppointmentRow)}
                {upcomingAppointments.length > 4 && (
                  <button
                    type="button"
                    className={styles.seeMoreButton}
                    onClick={() => setShowAllUpcoming((prev) => !prev)}
                  >
                    {showAllUpcoming ? 'See Less' : 'See All'}
                  </button>
                )}
              </>
            )}
          </div>
        </section>

        <section className={styles.card}>
          <div className={styles.cardHeader}>
            <h3 className={styles.cardTitle}>{translations('parked')} {translations('consultations')}</h3>
            <span className={styles.cardMeta}>{parkedConsultations.length}</span>
          </div>
          <div className={styles.cardBody}>
            {parkedConsultations.length === 0 ? (
              <div className={styles.emptyState}>
                {translations('placeholder.body.parked') ?? translations('no.data')}
              </div>
            ) : (
              <>
                {visibleParkedConsultations.map(renderParkedRow)}
                {parkedConsultations.length > 4 && (
                  <button
                    type="button"
                    className={styles.seeMoreButton}
                    onClick={() => setShowAllParked((prev) => !prev)}
                  >
                    {showAllParked ? 'See Less' : 'See All'}
                  </button>
                )}
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

